/**
 * DateInput wraps SingleDatePicker from React-dates and gives a list of all default props we use.
 * Styles for SingleDatePicker can be found from 'public/reactDates.css'.
 *
 * N.B. *isOutsideRange* in defaultProps is defining what dates are available to booking.
 */
import React, { useState } from 'react';
import { bool, func, instanceOf, shape, string } from 'prop-types';
import { SingleDatePicker } from 'react-dates';

// Import moment from moment-timezone. 10-year range only.
// The full data included in moment-timezone dependency is mostly irrelevant
// and slows down the first paint.
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';

import classNames from 'classnames';

import NextMonthIcon from './NextMonthIcon';
import PreviousMonthIcon from './PreviousMonthIcon';
import css from './DateInput.module.css';

export const HORIZONTAL_ORIENTATION = 'horizontal';
export const ANCHOR_LEFT = 'left';

function DateInputComponent(props) {
  const [focused, setFocused] = useState(false);

  const {
      className = null,
      initialDate = null,
      name,
      placeholderText = null,
      onBlur,
      onChange,
      onFocus,
      screenReaderInputMessage = null,
      useMobileMargins = false,
      value = null,
      _children = null,
      _render = null,
      
      // Default props that were formerly in defaultProps
      id = 'dateInput',
      placeholder = null,
      disabled = false,
      required = false,
      readOnly = false,
      // screenReaderInputMessage handled above
      showClearDate = false,
      customCloseIcon = null,
      showDefaultInputIcon = false,
      customInputIcon = null,
      noBorder = true,
      block = false,
      small = false,
      renderMonthText = null,
      orientation = HORIZONTAL_ORIENTATION,
      anchorDirection = ANCHOR_LEFT,
      horizontalMargin = 0,
      withPortal = false,
      withFullScreenPortal = false,
      appendToBody = false,
      disableScroll = false,
      initialVisibleMonth = null,
      firstDayOfWeek = 0,
      numberOfMonths = 1,
      keepOpenOnDateSelect = false,
      reopenPickerOnClearDate = false,
      renderCalendarInfo = null,
      hideKeyboardShortcutsPanel = false,
      daySize = 39,
      isRTL = false,
      verticalHeight = null,
      transitionDuration = undefined,
      verticalSpacing = 46,
      navPrev = <PreviousMonthIcon />,
      navNext = <NextMonthIcon />,
      onPrevMonthClick = () => {},
      onNextMonthClick = () => {},
      renderCalendarDay = undefined,
      renderDayContents = null,
      enableOutsideDays = false,
      isDayHighlighted = () => false,
      displayFormat = () => moment.localeData().longDateFormat('L'),
      monthFormat = 'MMMM YYYY',
      phrases = {},
      weekDayFormat = 'dd',

      ...other
  } = props;
  
  const isOutsideRange = props.isOutsideRange || (day => !isInRange(day, moment(), moment().add(10, 'years')));
  const isDayBlocked = props.isDayBlocked || (() => false);

  const datePickerProps = {
      id, placeholder, disabled, required, readOnly,
      showClearDate, customCloseIcon, showDefaultInputIcon, customInputIcon, noBorder, block, small,
      renderMonthText, orientation, anchorDirection, horizontalMargin, withPortal, withFullScreenPortal, appendToBody,
      disableScroll, initialVisibleMonth, firstDayOfWeek, numberOfMonths, keepOpenOnDateSelect, reopenPickerOnClearDate,
      renderCalendarInfo, hideKeyboardShortcutsPanel, daySize, isRTL, verticalHeight, transitionDuration, verticalSpacing,
      navPrev, navNext, onPrevMonthClick, onNextMonthClick, renderCalendarDay, renderDayContents, enableOutsideDays,
      isDayHighlighted, displayFormat, monthFormat, phrases, weekDayFormat,
      isOutsideRange, isDayBlocked,
      ...other,
  };

  const initialMoment = initialDate ? moment(initialDate) : null;
  const date =
    value && value.date instanceof Date && value.date.toString() !== 'Invalid Date'
      ? moment(value.date)
      : initialMoment;

  // Inline i18n: use English placeholder and screen reader text
  const placeholderLabel = placeholderText || 'Select date';
  const screenReaderInputText =
    screenReaderInputMessage ||
    'Choose a date. Press the down arrow key to interact with the calendar and select a date. Press the escape key to close the calendar.';

  const classes = classNames(css.inputRoot, className, {
    [css.withMobileMargins]: useMobileMargins,
  });

  const handleDateChange = date => {
    const selectedDate = moment && moment.isMoment(date) ? date.toDate() : null;
    onChange({ date: selectedDate });
  };

  const handleFocusChange = values => {
    const isFocused = values.focused;
    setFocused(isFocused);
    if (isFocused) {
      onFocus();
    } else {
      onBlur();
    }
  };

  return (
    <div className={classes}>
      <SingleDatePicker
        {...datePickerProps}
        focused={focused}
        onFocusChange={handleFocusChange}
        date={date}
        onDateChange={handleDateChange}
        placeholder={placeholderLabel}
        screenReaderInputMessage={screenReaderInputText}
      />
    </div>
  );
}

DateInputComponent.propTypes = {
  className: string,
  id: string,
  focused: bool,
  initialDate: instanceOf(Date),
  name: string.isRequired,
  onChange: func.isRequired,
  onBlur: func.isRequired,
  onFocus: func.isRequired,
  isDayBlocked: func,
  useMobileMargins: bool,
  placeholderText: string,
  screenReaderInputMessage: string,
  value: shape({
    date: instanceOf(Date),
  }),
};

export default DateInputComponent;
